import { getCustomsDeclarationsTotalWeight } from "@redotech/redo-model/fulfillments/customs-declaration";
import { RedoShopifyDeliveryMethodType } from "@redotech/redo-model/fulfillments/fulfillment-delivery-method-type";
import {
  FulfillmentGroupData,
  getSelectedRate,
} from "@redotech/redo-model/fulfillments/fulfillment-group";
import { FulfillmentOrderAddress } from "@redotech/redo-model/fulfillments/fulfillment-order-address";
import { OMSUser } from "@redotech/redo-model/fulfillments/oms-user";
import { TiktokShippingTypeTag } from "@redotech/redo-model/fulfillments/outbound-shipment";
import {
  DEFAULT_INSURANCE_INCLUDED_THRESHOLD,
  DEFAULT_INSURANCE_RATE_PERCENTAGE,
  getTotalInsuranceCostForGroups,
} from "@redotech/redo-model/outbound-labels/fulfillment-group-insurance";
import { LearnedPresetKind } from "@redotech/redo-model/outbound-labels/learned-preset";
import {
  OutboundRate,
  shouldChargeForLabel,
} from "@redotech/redo-model/outbound-labels/outbound-labels";
import {
  applyParcelWeightDistribution,
  areParcelsEqual,
  OutboundParcel,
  Parcel,
} from "@redotech/redo-model/outbound-labels/parcel";
import { Surcharge } from "@redotech/redo-model/outbound-labels/surcharge";
import { Weight, WeightUnit } from "@redotech/redo-model/outbound-labels/util";
import { TeamContextValue } from "@redotech/redo-web/context/team-context";
import { useMemo } from "react";
import {
  OutboundRateOption,
  summarizeOutboundRateOptions,
} from "../common/util";
import { getTrackingNumbers } from "../fulfillment-groups/columns/tracking-number-column";

export type DisplayParcel = OutboundParcel & { hasWeightOverride: boolean };

export interface UseGroupInfoResponse {
  selectedRate: OutboundRateOption | null;
  mixedRates: boolean;
  allSelectedRates: (OutboundRate | null)[];
  deliveryMethod: RedoShopifyDeliveryMethodType | null | undefined;
  availableRates: readonly OutboundRateOption[];

  selectedParcelsPerGroup: DisplayParcel[][];
  allowMultipleParcels: boolean;
  mixedParcels: boolean;
  mixedWeight: boolean;
  canBulkEditParcels: boolean;

  assignedUser: OMSUser | null;
  mixedUsers: boolean;

  totalWeight: Weight | null;

  ratesPending: boolean;

  totalCustomsDeclarationWeight: Weight | null | undefined;

  destinationAddress: FulfillmentOrderAddress | null;
  originAddress: FulfillmentOrderAddress | null;
  insuranceCost: number;
  totalRatePrice: number | null;
  totalChargedToRedoBalance: number | null;

  usedShippingLearning: boolean;
  usedWeightLearning: boolean;
  usedPackageLearning: boolean;

  trackingNumbers?: string[];
}

export function useFulfillmentGroupUsedLearning(
  fulfillmentGroups: readonly FulfillmentGroupData[],
) {
  const usedShippingLearning: boolean = useMemo(() => {
    return fulfillmentGroups.every(
      (group) => group?.usedLearning?.[LearnedPresetKind.CARRIER_SERVICE_LEVEL],
    );
  }, [fulfillmentGroups]);

  const usedWeightLearning = useMemo(() => {
    return fulfillmentGroups.every(
      (group) => group?.usedLearning?.[LearnedPresetKind.WEIGHT],
    );
  }, [fulfillmentGroups]);

  const usedPackageLearning = useMemo(() => {
    return fulfillmentGroups.every(
      (group) => group?.usedLearning?.[LearnedPresetKind.PARCEL],
    );
  }, [fulfillmentGroups]);

  return { usedShippingLearning, usedWeightLearning, usedPackageLearning };
}

export function useContainsTiktokShippingOrder(
  fulfillmentGroups: readonly FulfillmentGroupData[],
): boolean {
  return useMemo(() => {
    return fulfillmentGroups.some((group) =>
      group.orders.some((order) =>
        order.tags.includes(TiktokShippingTypeTag.TIKTOK),
      ),
    );
  }, [fulfillmentGroups]);
}

export function useFulfillmentGroupAdjustments(
  fulfillmentGroups: readonly FulfillmentGroupData[],
): (Surcharge & { balanceTransactionId: string })[] {
  return useMemo(() => {
    if (fulfillmentGroups.length !== 1) {
      return [];
    }
    const fg = fulfillmentGroups[0]!;
    return [
      ...(fg.shipment?.surcharges ?? []),
      ...(fg.additionalShipments?.flatMap((s) => s.surcharges ?? []) ?? []),
    ];
  }, [fulfillmentGroups]);
}

export function useSelectedRate(
  fulfillmentGroups: readonly FulfillmentGroupData[],
  permissionsToEditEveryGroup: boolean,
): {
  selectedRate: OutboundRateOption | null;
  mixedRates: boolean;
  allSelectedRates: (OutboundRate | null)[];
  deliveryMethod: RedoShopifyDeliveryMethodType | null | undefined;
  availableRates: readonly OutboundRateOption[];
} {
  const availableRates: readonly OutboundRateOption[] = useMemo(() => {
    if (permissionsToEditEveryGroup) {
      return summarizeOutboundRateOptions(fulfillmentGroups);
    }
    return [];
  }, [fulfillmentGroups, permissionsToEditEveryGroup]);

  const { selectedRate, mixedRates, allSelectedRates, deliveryMethod } =
    useMemo(() => {
      const allSelectedRates = fulfillmentGroups.map((group) => {
        const rate = getSelectedRate({
          selectedShippingRate: group.selectedShippingRate,
          availableRates: group.availableShipmentRates?.rates,
        });

        if (group.fulfillmentInfo?.numericRate && rate) {
          return {
            ...rate,
            rate: group.fulfillmentInfo.numericRate.toFixed(2),
          };
        }
        return rate ?? null;
      });
      const allDeliveryMethods = fulfillmentGroups.map(
        (group) => group.deliveryMethod,
      );
      const deliveryMethod = allDeliveryMethods.every(
        (method) => method === allDeliveryMethods[0],
      )
        ? allDeliveryMethods[0]
        : null;

      // Check if carrier/service/redoRate are the same (ignoring carrier_account_id)
      const selectedRateKeysWithoutCarrierAccountId = allSelectedRates.map(
        (rate) =>
          rate
            ? `${rate.carrier}-${rate.service}-${!rate.is_merchant_carrier_account}`
            : null,
      );

      // Check if carrier_account_id is also the same (for exact matching)
      const selectedRateKeysWithCarrierAccountId = allSelectedRates.map(
        (rate) =>
          rate
            ? `${rate.carrier}-${rate.service}-${!rate.is_merchant_carrier_account}-${rate.carrier_account_id}`
            : null,
      );

      if (
        !selectedRateKeysWithoutCarrierAccountId.every(
          (key) => selectedRateKeysWithoutCarrierAccountId[0] === key,
        ) ||
        !deliveryMethod
      ) {
        return {
          selectedRate: null,
          mixedRates: true,
          allSelectedRates,
          deliveryMethod,
        };
      }

      // If carrier/service/redoRate match, try to find the matching rate
      const keyWithCarrierAccountId = selectedRateKeysWithCarrierAccountId[0];
      const keyWithoutCarrierAccountId =
        selectedRateKeysWithoutCarrierAccountId[0];

      // First try to find exact match with carrier_account_id (single group case)
      let rateOption =
        availableRates.find((rate) => rate.key === keyWithCarrierAccountId) ??
        null;

      // If not found, try to find multi-group rate (ends with -multiple)
      if (!rateOption) {
        rateOption =
          availableRates.find(
            (rate) => rate.key === `${keyWithoutCarrierAccountId}-multiple`,
          ) ?? null;
      }

      // If still not found (e.g., different carrier account IDs across groups in single-group mode),
      // find any rate that matches carrier/service/redoRate
      if (!rateOption) {
        rateOption =
          availableRates.find((rate) =>
            rate.key?.startsWith(`${keyWithoutCarrierAccountId}-`),
          ) ?? null;
      }

      // Surface a purchased label's frozen snapshot on the display option.
      // Single-group only — multi-group views share one rate and would mix them.
      const struck =
        fulfillmentGroups.length === 1
          ? fulfillmentGroups[0]?.fulfillmentInfo?.rateMoney?.struck
          : undefined;

      return {
        selectedRate:
          rateOption && struck ? { ...rateOption, struck } : rateOption,
        mixedRates: false,
        allSelectedRates,
        deliveryMethod,
      };
    }, [fulfillmentGroups, availableRates]);

  return {
    selectedRate,
    mixedRates,
    allSelectedRates,
    deliveryMethod,
    availableRates,
  };
}

export function useGroupInfo({
  availableUsers,
  permissionsToEditEveryGroup,
  parcelValues,
  fulfillmentGroups,
  team,
}: {
  fulfillmentGroups: readonly FulfillmentGroupData[];
  availableUsers: OMSUser[];
  permissionsToEditEveryGroup: boolean;
  parcelValues: readonly Parcel[];
  team: TeamContextValue;
}): UseGroupInfoResponse {
  const {
    selectedRate,
    mixedRates,
    allSelectedRates,
    deliveryMethod,
    availableRates,
  } = useSelectedRate(fulfillmentGroups, permissionsToEditEveryGroup);
  const {
    selectedParcelsPerGroup,
    mixedParcels,
    mixedWeight,
    canBulkEditParcels,
  } = useMemo(() => {
    const defaultParcel = parcelValues.find((parcel) => parcel.default);
    const populatedParcelLists = fulfillmentGroups.map((group) => {
      const rawParcels =
        group.selectedParcels ?? (defaultParcel ? [defaultParcel] : []);
      const { parcels } = applyParcelWeightDistribution(
        rawParcels,
        group.items,
      );
      return parcels.map(
        (parcel, i): DisplayParcel => ({
          ...parcel,
          hasWeightOverride: !!(rawParcels[i] as OutboundParcel | undefined)
            ?.parcelAndContentWeight,
        }),
      );
    });

    const firstPopulatedParcelList = populatedParcelLists[0];

    const mixedWeight =
      !firstPopulatedParcelList ||
      !populatedParcelLists.every(
        (parcelList) =>
          parcelList.length === firstPopulatedParcelList.length &&
          parcelList.every(
            (parcel, index) =>
              parcel.parcelAndContentWeight?.value ===
                firstPopulatedParcelList[index]?.parcelAndContentWeight
                  ?.value &&
              parcel.parcelAndContentWeight?.unit ===
                firstPopulatedParcelList[index]?.parcelAndContentWeight?.unit,
          ),
      );

    const mixedParcels = !populatedParcelLists.every((parcelList) =>
      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      areParcelsEqual(firstPopulatedParcelList, parcelList),
    );

    const canBulkEditParcels =
      fulfillmentGroups.length <= 1 ||
      populatedParcelLists.every(
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        (parcelList) => parcelList.length === firstPopulatedParcelList.length,
      );

    return {
      selectedParcelsPerGroup: populatedParcelLists,
      mixedParcels,
      mixedWeight,
      canBulkEditParcels,
    };
  }, [parcelValues, fulfillmentGroups]);

  const allowMultipleParcels = !mixedParcels && !mixedWeight;

  const { assignedUser, mixedUsers } = useMemo(() => {
    const users = fulfillmentGroups.map((group) => group.assignedUser);

    if (users.every((user) => user == null)) {
      return { assignedUser: null, mixedUsers: false };
    }

    if (users.length === 1 && users[0]) {
      return { assignedUser: users[0] ?? null, mixedUsers: false };
    }
    const firstUser = users[0] ?? null;
    if (users.every((user) => firstUser?._id === user?._id)) {
      return { assignedUser: firstUser, mixedUsers: false };
    }
    return { assignedUser: null, mixedUsers: true };
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableUsers, fulfillmentGroups]);

  // Total weight of each package, not all of them combined.
  // Null if mixed weights.
  // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
  const totalWeight = useMemo<Weight | null>(() => {
    const weights = fulfillmentGroups.map((group) => {
      return { value: group.displayWeightGrams ?? 0, unit: WeightUnit.GRAM };
    });

    // TODO: consider "equivalent" weights of different units equal.
    // "Equivalent" meaning we don't care to split ounces.
    if (
      weights.every(
        (other) =>
          // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
          other.unit === weights[0].unit && other.value === weights[0].value,
      )
    ) {
      return weights[0];
    }
    return null;
  }, [fulfillmentGroups]);

  const ratesPending = useMemo(() => {
    if (permissionsToEditEveryGroup) {
      return fulfillmentGroups.some((group) => !!group.ratesPendingSince);
    }
    return false;
  }, [fulfillmentGroups, permissionsToEditEveryGroup]);

  const totalCustomsDeclarationWeight = useMemo(() => {
    return fulfillmentGroups.length === 1 &&
      fulfillmentGroups?.[0]?.customs?.declarations
      ? getCustomsDeclarationsTotalWeight(
          fulfillmentGroups[0].customs.declarations,
        )
      : null;
  }, [fulfillmentGroups]);

  const destinationAddress = useMemo(() => {
    return fulfillmentGroups.length === 1
      ? fulfillmentGroups?.[0]?.destinationAddress
      : null;
  }, [fulfillmentGroups]);

  const originAddress = useMemo(() => {
    return fulfillmentGroups.length === 1
      ? fulfillmentGroups?.[0]?.originAddress
      : null;
  }, [fulfillmentGroups]);

  const insuranceCost = useMemo(() => {
    const teamRatePercentage =
      team?.settings?.outboundLabels?.insuranceRatePercentage ??
      DEFAULT_INSURANCE_RATE_PERCENTAGE;
    const teamInsuranceIncludedThreshold =
      team?.settings?.outboundLabels?.insuranceIncludedThreshold ??
      DEFAULT_INSURANCE_INCLUDED_THRESHOLD;

    return getTotalInsuranceCostForGroups({
      groups: fulfillmentGroups,
      teamRatePercentage,
      teamInsuranceIncludedThreshold,
    });
  }, [
    fulfillmentGroups,
    team?.settings?.outboundLabels?.insuranceRatePercentage,
    team?.settings?.outboundLabels?.insuranceIncludedThreshold,
  ]);

  const { totalRatePrice, totalChargedToRedoBalance } = useMemo(() => {
    const usedRates = allSelectedRates.filter((rate) => rate !== null);

    if (usedRates.length === 0) {
      return { totalRatePrice: null, totalChargedToRedoBalance: null };
    }

    const totalUninsuredRate = usedRates.reduce(
      (sum, rate) => sum + Number(rate?.rate),
      0,
    );

    const uninsuredAmountChargedToRedoBalance = usedRates.reduce(
      (sum, rate) => {
        if (rate && shouldChargeForLabel(rate)) {
          return sum + Number(rate?.rate);
        }
        return sum;
      },
      0,
    );

    return {
      totalRatePrice: totalUninsuredRate + insuranceCost,
      totalChargedToRedoBalance:
        uninsuredAmountChargedToRedoBalance + insuranceCost,
    };
  }, [allSelectedRates, insuranceCost]);

  const { usedShippingLearning, usedWeightLearning, usedPackageLearning } =
    useFulfillmentGroupUsedLearning(fulfillmentGroups);

  // Tracking numbers should only show up if there's only one group
  const trackingNumbers = useMemo(() => {
    if (fulfillmentGroups.length !== 1) {
      return undefined;
    }
    // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
    return getTrackingNumbers(fulfillmentGroups[0]);
  }, [fulfillmentGroups]);

  return {
    selectedRate,
    mixedRates,
    allSelectedRates,
    deliveryMethod,
    availableRates,
    selectedParcelsPerGroup,
    allowMultipleParcels,
    mixedParcels,
    mixedWeight,
    canBulkEditParcels,
    assignedUser,
    mixedUsers,
    totalWeight,
    ratesPending,
    totalCustomsDeclarationWeight,
    // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
    destinationAddress,
    // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
    originAddress,
    insuranceCost,
    totalRatePrice,
    totalChargedToRedoBalance,
    usedShippingLearning,
    usedWeightLearning,
    usedPackageLearning,
    trackingNumbers,
  };
}
